import { html, nothing } from "lit";
import type { SessionTraceResult, TraceSpan } from "../../../../src/infra/session-trace-types.ts";

export type { SessionTraceResult, TraceSpan };

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const traceStyles = `
  .trace-view {
    margin-top: 16px;
  }

  /* KPI row */
  .trace-kpis {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 14px;
  }
  .trace-kpi {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 14px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: 8px;
    min-width: 100px;
  }
  .trace-kpi-label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--muted);
  }
  .trace-kpi-value {
    font-size: 16px;
    font-weight: 700;
    font-family: var(--mono);
    color: var(--text-strong);
  }
  .trace-kpi-sub {
    font-size: 10px;
    color: var(--muted);
  }

  /* Toggle: flat vs tree */
  .trace-view-mode {
    display: flex;
    gap: 6px;
    margin-bottom: 12px;
  }
  .trace-view-mode .toggle-btn {
    padding: 5px 12px;
    border-radius: 999px;
    border: 1px solid var(--border);
    background: var(--bg-secondary);
    font-size: 12px;
    cursor: pointer;
    color: var(--text-muted);
    transition: all 0.15s;
  }
  .trace-view-mode .toggle-btn:hover {
    color: var(--text);
    border-color: var(--border-strong);
  }
  .trace-view-mode .toggle-btn.active {
    background: var(--accent);
    border-color: var(--accent);
    color: #fff;
  }

  /* Span tree */
  .trace-tree {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  /* Span row */
  .trace-span-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 7px 10px;
    border-radius: 8px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: background0.12s, border-color 0.12s;
    font-size: 13px;
  }
  .trace-span-row:hover {
    background: var(--bg-hover);
    border-color: var(--border);
  }
  .trace-span-row.expanded {
    background: var(--bg-secondary);
    border-color: var(--border);
  }

  /* Expand chevron */
  .trace-expand-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
    color: var(--muted);
    padding: 0;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    flex-shrink: 0;
    transition: background 0.12s, color 0.12s;
  }
  .trace-expand-btn:hover {
    background: var(--bg-hover);
    color: var(--text);
  }
  .trace-expand-btn.empty {
    visibility: hidden;
  }

  /* Kind icon */
  .trace-kind-icon {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .trace-kind-icon.agent-cycle {
    background: rgba(255, 77, 77, 0.12);
    color: #ff4d4d;
  }
  .trace-kind-icon.model-call {
    background: rgba(59, 130, 246, 0.12);
    color: #3b82f6;
  }
  .trace-kind-icon.tool-call {
    background: rgba(16, 185, 129, 0.12);
    color: #10b981;
  }
  .trace-kind-icon.tool-result {
    background: rgba(245, 158, 11, 0.12);
    color: #f59e0b;
  }

  /* Span body */
  .trace-span-body {
    flex: 1 1 auto;
    min-width: 0;
 }
  .trace-span-header {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }
  .trace-span-label {
    font-weight: 600;
    font-size: 13px;
    color: var(--text);
  }
  .trace-span-detail {
    font-size: 11px;
    color: var(--muted);
  }
  .trace-span-preview {
    font-size: 11px;
    color: var(--muted);
    margin-top: 3px;
    white-space: pre-wrap;
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    max-width: 480px;
  }

  /* Span metrics */
  .trace-span-metrics {
    display: flex;
    gap: 10px;
    flex-shrink: 0;
    margin-left: auto;
    flex-wrap: wrap;
    align-items: center;
  }
  .trace-metric {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
  }
  .trace-metric-value {
    font-size: 12px;
    font-weight: 600;
    font-family: var(--mono);
    color: var(--text);
  }
  .trace-metric-label {
    font-size: 10px;
    color: var(--muted);
  }
  .trace-metric-value.error {
    color: var(--danger);
  }

  /* Children (indented) */
  .trace-children {
    display: flex;
    flex-direction: column;
    gap: 3px;
    margin-left: 24px;
    padding-left: 12px;
    border-left: 2px solid var(--border);
    margin-top: 4px;
  }

  /* Flat list mode */
  .trace-flat {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .trace-flat-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: var(--bg-secondary);
    font-size: 12px;
  }
  .trace-flat-item .trace-kind-icon {
    margin-top: 2px;
  }
  .trace-flat-body {
    flex: 1;
    min-width: 0;
  }
  .trace-flat-label {
    font-weight: 600;
    font-size: 12px;
 }
  .trace-flat-meta {
    font-size: 11px;
    color: var(--muted);
    margin-top: 2px;
  }
  .trace-flat-metrics {
    display: flex;
    gap: 10px;
    flex-shrink: 0;
    align-items: center;
  }

  /* Loading */
  .trace-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    gap: 10px;
    color: var(--muted);
    font-size: 13px;
  }
  .trace-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid var(--border);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Empty */
  .trace-empty {
    padding: 32px;
    text-align: center;
    color: var(--muted);
    font-size: 13px;
  }
  .trace-empty-icon { font-size: 32px; opacity: 0.2; margin-bottom: 8px; }
  .trace-empty-title { font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
  .trace-empty-desc { font-size: 12px; line-height: 1.5; }
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const KIND_ICONS: Record<string, string> = {
  "agent-cycle": "🔄",
  "model-call": "💬",
  "tool-call": "🔧",
  "tool-result": "📤",
};

const KIND_LABELS: Record<string, string> = {
  "agent-cycle": "Agent Cycle",
  "model-call": "Model Call",
  "tool-call": "Tool Call",
  "tool-result": "Tool Result",
};

const fmtNum = (n: number | undefined) => {
  if (!n && n !== 0) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const fmtUsd = (n: number | undefined) => {
  if (!n) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 4,
  }).format(n);
};

const fmtMs = (ms: number | undefined) => {
  if (!ms) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const fmtTs = (ms: number) => {
  const d = new Date(ms);
  return d.toISOString().replace("T", " ").slice(0, 19) + "Z";
};

// ---------------------------------------------------------------------------
// Span row (recursive)
// ---------------------------------------------------------------------------

type SpanRowState = {
  expanded: Set<string>;
  toggle: (id: string) => void;
};

function renderSpanRow(
  span: TraceSpan,
  state: SpanRowState,
  depth: number,
): ReturnType<typeof html> {
  const isExpanded = state.expanded.has(span.id);
  const hasChildren = span.children.length > 0;
  const iconClass = span.kind.replace(/\./g, "-");

  return html`
<div class="trace-span-row ${isExpanded ? "expanded" : ""}">
      <button
        class="trace-expand-btn ${!hasChildren ? "empty" : ""}"
        @click=${() => hasChildren && state.toggle(span.id)}
        title=${hasChildren ? (isExpanded ? "Collapse" : "Expand") : ""}
      >
        ${hasChildren ? (isExpanded ? "▾" : "▸") : ""}
      </button>

      <div class="trace-kind-icon ${iconClass}" title=${KIND_LABELS[span.kind] ?? span.kind}>
        ${KIND_ICONS[span.kind] ?? "·"}
      </div>

      <div class="trace-span-body">
        <div class="trace-span-header">
          <span class="trace-span-label">${span.label}</span>
          ${span.detail ? html`<span class="trace-span-detail">${span.detail}</span>` : nothing}
        </div>
        ${
          span.contentPreview
            ? html`<div class="trace-span-preview">${span.contentPreview}</div>`
            : nothing
        }
      </div>

      <div class="trace-span-metrics">
        ${
          span.totalTokens
            ? html`
              <div class="trace-metric">
                <span class="trace-metric-value">${fmtNum(span.totalTokens)}</span>
                <span class="trace-metric-label">tokens</span>
              </div>
            `
            : nothing
        }
        ${
          span.cost !== undefined
            ? html`
              <div class="trace-metric">
                <span class="trace-metric-value">${fmtUsd(span.cost)}</span>
                <span class="trace-metric-label">cost</span>
              </div>
            `
            : nothing
        }
        ${
          span.durationMs > 0
            ? html`
              <div class="trace-metric">
                <span class="trace-metric-value">${fmtMs(span.durationMs)}</span>
                <span class="trace-metric-label">duration</span>
              </div>
            `
            : nothing
        }
        ${
          span.inputTokens !== undefined && span.outputTokens !== undefined
            ? html`
              <div class="trace-metric">
                <span class="trace-metric-value">${fmtNum(span.inputTokens)} / ${fmtNum(span.outputTokens)}</span>
                <span class="trace-metric-label">in / out</span>
              </div>
            `
            : nothing
        }
        ${
          span.error
            ? html`
                <span class="trace-metric-value error">⚠ error</span>
              `
            : nothing
        }
      </div>
    </div>

    ${
      isExpanded && hasChildren
        ? html`
          <div class="trace-children">
            ${span.children.map((child) => renderSpanRow(child, state, depth + 1))}
          </div>
        `
        : nothing
    }
  `;
}

// ---------------------------------------------------------------------------
// Main render
// ---------------------------------------------------------------------------

export type TraceViewProps = {
  trace: SessionTraceResult | null;
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  expanded?: Set<string>;
  onToggleExpand?: (id: string) => void;
};

export function renderTraceView(props: TraceViewProps) {
  const { trace, loading, error, expanded, onToggleExpand } = props;
  const toggleExpand = onToggleExpand ?? ((_id: string) => {});

  return html`
    <style>${traceStyles}</style>
    <div class="trace-view">
      ${
        loading
          ? html`
              <div class="trace-loading">
                <div class="trace-spinner"></div>
                Loading trace…
              </div>
            `
          : error
            ? html`
              <div class="trace-empty">
                <div class="trace-empty-icon">⚠</div>
                <div class="trace-empty-title">Failed to load trace</div>
                <div class="trace-empty-desc">${error}</div>
                ${
                  props.onRetry
                    ? html`
                      <button class="btn" style="margin-top:12px" @click=${props.onRetry}>
                        Retry
                      </button>
                    `
                    : nothing
                }
              </div>
            `
            : !trace || trace.spans.length === 0
              ? html`
                  <div class="trace-empty">
                    <div class="trace-empty-icon">🔍</div>
                    <div class="trace-empty-title">No trace data</div>
                    <div class="trace-empty-desc">No agent cycles found in this session transcript.</div>
                  </div>
                `
              : html`
                <!-- KPI row -->
                <div class="trace-kpis">
                  <div class="trace-kpi">
                    <div class="trace-kpi-label">Total Cost</div>
                    <div class="trace-kpi-value">${fmtUsd(trace.totalCost)}</div>
                  </div>
                  <div class="trace-kpi">
                    <div class="trace-kpi-label">Total Tokens</div>
                    <div class="trace-kpi-value">${fmtNum(trace.totalTokens)}</div>
                  </div>
                  <div class="trace-kpi">
                    <div class="trace-kpi-label">Agent Cycles</div>
                    <div class="trace-kpi-value">${trace.cycleCount}</div>
                  </div>
                  <div class="trace-kpi">
                    <div class="trace-kpi-label">Model Calls</div>
                    <div class="trace-kpi-value">${trace.modelCallCount}</div>
                  </div>
                  <div class="trace-kpi">
                    <div class="trace-kpi-label">Tool Calls</div>
                    <div class="trace-kpi-value">${trace.toolCallCount}</div>
                  </div>
                  <div class="trace-kpi">
                    <div class="trace-kpi-label">Duration</div>
                    <div class="trace-kpi-value">${fmtMs(trace.totalDurationMs)}</div>
                  </div>
                </div>

                <!-- Tree -->
                <div class="trace-tree">
                  ${trace.spans.map((span) =>
                    renderSpanRow(
                      span,
                      { expanded: expanded ?? new Set(), toggle: toggleExpand },
                      0,
                    ),
                  )}
                </div>
              `
      }
    </div>
  `;
}
